import { Registry } from '@react-registry'
import { deserialize as defaultDeserialize } from './deserializer/defaultDeserializer'
import { deserialize as googleDeserialize } from './deserializer/googleDocsSerializer'
import { deserialize as csDeserialize } from './deserializer/superchargedRTEDeserializer'
import { deserialize as wordDeserialize } from './deserializer/wordDeserializer'
import { deserialize as onlineWordDeserialize } from './deserializer/onlineWordDeserializer'
import collapse from "../../../../../../utils/collapse-whitespace"
import { applyDirty, preInsert, preInsertTable } from './utils'
import { Transforms } from 'slate'
import { getRegistry } from '../../../utils/getRegistry'
import { detect } from 'detect-browser'
import slateFragmentDeserializer, { decodeSlateFragmentData } from './deserializer/slateFragmentDeserializer'

export const getCustomInserts = () => {
  return Object.entries(getRegistry('v2.withPreInsert')).map(
    ([key, value]) => {
      return [key, value['components'][0]['component']]
    }
  )
}

const getUserAgentDetails = () => {
  const systemDetails = detect()
  return {
    platform: systemDetails.os,
    browser: systemDetails.name,
    browserVersion: systemDetails.version
  }
}

export const removeEmptyLinesFromTextAndConvertIntoParagraph = (text: string) => {
  // Collapse multiple new lines into a single new line
  const cleanedText = text.replace(/\n{2,}/g, '\n')
  const lines = cleanedText.split('\n').filter(line => line.trim() !== '');
  const nodes = lines.map(line => ({
    type: 'p',
    children: [{ text: line }],
  }));
  return nodes;
}

const getFragmentFromCopyPasteScript = (copyPasteConfig, docTypeFromScript, parsedHtml, wordPasteOptions, editor) => {
  try {
    const userAgentDetails = getUserAgentDetails()
    const docCopyPasteConfig = copyPasteConfig?.docCopyPasteConfig
    if (!docCopyPasteConfig?.config || !docTypeFromScript) return null
    let configMatch = Array.from(docCopyPasteConfig.config).find((config: any) => {
      return config.type === docTypeFromScript.docType &&
        (config?.browser && userAgentDetails.browser.includes(config?.browser)) &&
        (config?.platform && userAgentDetails.platform.includes(config?.platform)) &&
        config?.mode === docTypeFromScript.mode &&
        config.shouldVisit(parsedHtml)
    })
    if (configMatch) {
      //@ts-ignore
      return configMatch.parser(parsedHtml, wordPasteOptions, editor)
    }
    return null
  } catch (err) {
    console.log("Error while copy pasting data using external script", err)
  }
}


const pattern = /^docs-internal-guid/
const withHtml = editor => {

  const { insertData } = editor

  editor.insertData = async data => {
    const customPasteOptions = editor.customPasteOptions || {}
    const copyPasteConfig = editor.copyPasteConfig || {}
    const wordPasteOptions: object = customPasteOptions && customPasteOptions.wordDoc || {}
    const googlePasteOptions = customPasteOptions && customPasteOptions.googleDoc || {}

    const html = data.getData('text/html');
    const dataTypes = data.types;

    if (html) {
      const htmlDom = new DOMParser().parseFromString(html, 'text/html')
      let parsedHtml: any = htmlDom.body;
      let docTypeFromScript = copyPasteConfig?.findDocType?.(parsedHtml)
      let fragment;
      let googlePlainHtml = "";

      let isGoogleDoc = Array.from(parsedHtml.childNodes).some((node: any) => {
        if (node.nodeName === 'B' && pattern.test(node['id'])) {
          googlePlainHtml = node.innerHTML
          return true
        }
        //for firefox
        if (pattern.test(node['id'])) {
          return true
        }
        return false
      })
      const isCSContent =
        parsedHtml.querySelector('[data-editor-type*="scrte"]')

      let shouldGetContentFromSlateFragment = false
      let slateFragmentData = null
      try {
        slateFragmentData = decodeSlateFragmentData(parsedHtml)?.[0]?.children // Get the children of the doc
        if (parsedHtml.querySelector('[data-slate-fragment]') && slateFragmentData?.length === 1) {

          slateFragmentData = slateFragmentDeserializer(parsedHtml, editor)
          while (slateFragmentData.children.length === 1 && slateFragmentData.children[0].children) {
            slateFragmentData = slateFragmentData.children[0]
          }
          if (!Array.isArray(slateFragmentData)) {
            slateFragmentData = [slateFragmentData]
          }
          shouldGetContentFromSlateFragment = true
        } else if ((parsedHtml.querySelector('[data-slate-fragment]') && slateFragmentData?.length > 1)) {
          slateFragmentData = slateFragmentDeserializer(parsedHtml, editor)
          slateFragmentData = slateFragmentData.children
          shouldGetContentFromSlateFragment = true;
        }
      }
      catch (err) {
        shouldGetContentFromSlateFragment = false
        console.error("Error while parsing slate fragment data", err)
      }

      if (!isCSContent) {
        collapse(parsedHtml)
      }
      const isExcel = htmlDom.querySelector('[xmlns\\:x="urn:schemas-microsoft-com:office:excel"]')
      const isWord = !isExcel &&
        (parsedHtml.getElementsByTagName('O:P').length ||
          parsedHtml.querySelector('[class*="Mso"]') || parsedHtml.querySelector('[style*="mso"]'))
      const isOnlineWord = parsedHtml.querySelector('[class*="BCX"]')

      const fragmentFromCopyPasteScript = getFragmentFromCopyPasteScript(copyPasteConfig, docTypeFromScript, parsedHtml, wordPasteOptions, editor)

      if (fragmentFromCopyPasteScript) {
        fragment = fragmentFromCopyPasteScript
      } else if (isGoogleDoc) {
        parsedHtml = googlePlainHtml ? new DOMParser().parseFromString(googlePlainHtml, 'text/html').body : parsedHtml
        fragment = googleDeserialize(parsedHtml, googlePasteOptions);
      } else if (shouldGetContentFromSlateFragment) {
        fragment = slateFragmentData
      } else if (isCSContent) {
        fragment = csDeserialize(parsedHtml, editor)
      } else if (isWord) {
        console.error("Error while loading word copy paste script. Running deserilizer from venus-components.")
        fragment = wordDeserialize(parsedHtml, wordPasteOptions, editor)
      } else if (isOnlineWord) {
        console.error("Error while loading word copy paste script. Running deserilizer from venus-components.")
        fragment = onlineWordDeserialize(parsedHtml)
      }
      else {
        fragment = defaultDeserialize(parsedHtml, editor);
      }

      if (fragment.length > 1) {
        fragment = preInsert(editor, fragment)
        let customInserts = getCustomInserts();
        if (customInserts?.length) {
          customInserts.forEach((element) => {
            try {
              fragment = element[1](editor, fragment)
              Array.isArray(fragment)
              fragment.every((element) => typeof element === 'object')
            }
            catch (err) {
              console.log("Invalid Fragment: ", err)
            }
          })
        }
      }
      else if (fragment.length === 1 && fragment[0].type === "table" && Array.isArray(editor.children)) {
        preInsertTable(fragment, editor.children?.[0].children?.length)
      }
      applyDirty(fragment);
      Transforms.insertFragment(editor, fragment);
      return;
    }
    else if (dataTypes && Array.isArray(dataTypes) && dataTypes.includes('text/plain') && dataTypes.length === 1) {
      const text = data.getData('text/plain');
      if (!text?.length) return;
      const nodes = removeEmptyLinesFromTextAndConvertIntoParagraph(text);
      if (!nodes?.length) return;
      return Transforms.insertFragment(editor, nodes);
    }
    insertData(data);
  }
  return editor
}
Registry.register(withHtml, { id: 'withHtml', registry: 'v2.plugin' })



